<template>
  <button
    type="button"
    title="Toggle Website Navigation Menu"
    id="user-dropdown-menu-toggle-button"
    ref="userDropdownMenuToggleButtonNode"
    @click="toggleDropdown()"
  ><i class="fas fa-fw fa-bars"></i></button>
  <DropdownMenu
    :nodes-excluded-from-closing-on-outside-click="userDropdownMenuToggleButtonNode ? [userDropdownMenuToggleButtonNode] : []"
    :dropdown-button-left-value="left"
    v-model:is-open="isOpen"
    v-model:max-body-height="dropdownHeight"
    :dropdown-menu-toggle-button-node="userDropdownMenuToggleButtonNode"
  >
    <template v-slot:body>
      <ul>
        <li>
          <a id="user-dropdown-menu-your-dashboard-button" href="/panels" @click="closeDropdown()">
            <i class="far fa-fw fa-tachometer-alt-fast"></i>
            &nbsp;Your Dashboard
          </a>
        </li>
        <li class="dropdown-divider"></li>
        <li>
          <a href="/freecharts/" @click="closeDropdown()">
            <i class="fas fa-fw fa-chart-line"></i>
            &nbsp;Charts &amp; Tools
          </a>
        </li>
        <li>
          <a href="/articles/" @click="closeDropdown()">
            <i class="far fa-fw fa-newspaper"></i>
            &nbsp;Articles
          </a>
        </li>
        <li>
          <a href="/tv/" @click="closeDropdown()">
            <i class="fas fa-fw fa-tv-retro"></i>
            &nbsp;StockCharts TV
          </a>
        </li>
        <li>
          <a href="//chartschool.stockcharts.com" rel="noopener noreferrer" @click="closeDropdown()">
            <i class="fas fa-fw fa-graduation-cap"></i>
            &nbsp;ChartSchool
          </a>
        </li>
        <li class="dropdown-divider"></li>
        <li>
          <a href="//help.stockcharts.com" rel="noopener noreferrer" @click="closeDropdown()">
            <i class="fas fa-fw fa-info-circle"></i>
            &nbsp;Help
          </a>
        </li>
        <li class="dropdown-divider"></li>
        <li>
          <button type="button" @click="initiateSiteSearchForm()">
            <i class="fas fa-fw fa-search"></i>
            &nbsp;Search
          </button>
          <form
            id="site-search-form"
            :class="{ 'site-search-form-visible': displaySiteSearchForm }"
            @submit.prevent="handleSiteSearchFormSubmit()"
          >
            <input
              type="text"
              placeholder="Search StockCharts"
              autocomplete="off"
              id="site-search-input"
              v-model="siteSearchText"
              ref="siteSearchInputElement"
            />
            <button
              v-if="trimmedSiteSearchText"
              type="submit"
              id="site-search-submit-button"
            >Go</button>
            <button
              v-else
              type="button"
              title="Close"
              id="site-search-close-button"
              @click="closeSiteSearchForm()"
            ><i class="fas fa-fw fa-times"></i></button>
          </form>
        </li>
      </ul>
    </template>
  </DropdownMenu>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';

  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const userDropdownMenuToggleButtonNode = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(userDropdownMenuToggleButtonNode, { windowResize: true });
  const siteSearchInputElement = ref<HTMLInputElement>();
  const displaySiteSearchForm = ref(false);
  const siteSearchText = ref('');
  const trimmedSiteSearchText = computed(() => {
    return typeof siteSearchText.value === 'string' ? siteSearchText.value.trim() : '';
  });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  function handleSiteSearchFormSubmit() {
    if (!trimmedSiteSearchText.value) {
      return;
    }

    window.location.href = `/search?q=${encodeURIComponent(trimmedSiteSearchText.value)}`;
  }

  function initiateSiteSearchForm() {
    siteSearchText.value = '';
    displaySiteSearchForm.value = true;

    siteSearchInputElement.value?.focus({ preventScroll: true });
  }

  function closeSiteSearchForm() {
    siteSearchText.value = '';
    displaySiteSearchForm.value = false;
  }

  watch(isOpen, () => {
    if (!isOpen.value) {
      userDropdownMenuToggleButtonNode.value?.blur();
      closeSiteSearchForm();
    }
  });
</script>

<style scoped>
  #user-dropdown-menu-toggle-button {
    margin: 0px;
    padding: 0px;
    font-size: 22px;
    background-color: transparent;
    border: none;
    border-radius: 0px;
    line-height: 35px;
    color: #d0dbe2;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
    min-width: 180px;
  }

  #site-search-form {
    width: 0%;
    display: inline-block;
    vertical-align: middle;
    overflow: hidden;
    transition: width 0.35s ease;
    white-space: nowrap;
    top: 0px;
    bottom: 0px;
    left: 0px;
    position: absolute;
    background-color: white;
  }

  #site-search-form.site-search-form-visible {
    width: 100%;
  }

  #site-search-input {
    height: 100%;
    display: inline-block;
    vertical-align: middle;
    width: calc(100% - 40px);
    padding-left: 10px;
    margin: 0px;
    background-color: transparent;
    border-radius: 0px;
    border-width: 1px;
    border-left: none;
    line-height: inherit;
    color: black;
    font-weight: 900;
  }

  #site-search-input::placeholder {
    text-transform: none;
    font-weight: 700;
    color:#707f8f;
  }

  #site-search-submit-button,
  #site-search-close-button {
    height: 100%;
    display: inline-block;
    vertical-align: middle;
    line-height: inherit;
    width: 40px;
    margin: 0px;
    background-color: transparent;
    border: none;
    border-radius: 0px;
    font-weight: 900;
    color: #5a6672;
    text-align: center;
  }

  #user-dropdown-menu-your-dashboard-button {
    font-weight: 900;
    color: #219155;
  }
</style>
